import React from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import StatsCard from "@/components/StatsCard";
import Img from "@/components/Img";
import { STATS, WHY_US, FOUNDERS } from "@/data/content";

export default function About() {
  const founder = FOUNDERS[0];
  return (
    <div data-testid="page-about" className="bg-[#FAF6F1]">

      <SEO
        title="About Petals & Promises — A Creative-Led Event Planning Studio"
        description="An event house built on care, calm, and craft. Studios in Gurgaon, Chandigarh, and Bangalore. We plan and design weddings, social celebrations, corporate events, and devotional gatherings with thoughtful styling and clear coordination."
        path="/about"
        keywords="about Petals & Promises, event planning studio India, creative-led event company, wedding planner Gurgaon, wedding planner Chandigarh, wedding planner Bangalore"
      />

      <section className="relative pt-28 md:pt-32 pb-16 md:pb-20 bg-[#F4DCD8]/35">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="pill"><Sparkles size={12} /> Our Story</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.98] mt-6 text-[#3B312D]">
              An event house built on <span className="italic text-[#B65980]">care, calm,</span> and craft.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[17px] leading-relaxed text-[#3B312D]/80">
              Petals &amp; Promises was founded with a simple belief: every celebration should feel personal for the host
              and effortless for the guest. We plan and design weddings, social celebrations, corporate events, and devotional
              gatherings with thoughtful styling, clear coordination, and a deep respect for every detail. Nothing is treated
              like a template. Every event is shaped around the people, the purpose, and the emotion behind it.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-5">
          {[
            { key: "about.gallery.1", defaultSrc: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=900&q=80", defaultAlt: "Indian wedding bride and groom varmala moment" },
            { key: "about.gallery.2", defaultSrc: "https://images.unsplash.com/photo-1604017011826-d3b4c23f8914?auto=format&fit=crop&w=900&q=80", defaultAlt: "Indian wedding mandap floral decor" },
            { key: "about.gallery.3", defaultSrc: "https://images.unsplash.com/photo-1758307390525-46a0825d4e50?auto=format&fit=crop&w=900&q=80", defaultAlt: "Premium Indian celebration tablescape and styling" },
            { key: "about.gallery.4", defaultSrc: "https://images.unsplash.com/photo-1530023367847-a683933f4172?auto=format&fit=crop&w=900&q=80", defaultAlt: "Luxury Indian wedding decor and tablescape" },
          ].map((img, i) => (
            <Reveal key={img.key} delay={i * 80} className="rounded-2xl overflow-hidden image-zoom h-56 md:h-72">
              <Img imageKey={img.key} defaultSrc={img.defaultSrc} defaultAlt={img.defaultAlt} className="w-full h-full object-cover" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 md:py-28 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="pill">Our Philosophy</p>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[#3B312D]">
              Restraint reads as <span className="italic text-[#B65980]">luxury</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[16px] text-[#3B312D]/85 leading-relaxed">
            <p>We build events around feeling, not noise. The meaningful moments should lead the experience, while every detail around them should support with grace.</p>
            <p>Refined palettes, thoughtful materials, calm hospitality, and clear coordination shape the standard we bring to every celebration, no matter the scale.</p>
            <p>Our promise: <span className="italic font-editorial text-[#3B312D]">&ldquo;Every celebration should feel effortless for the guest and unforgettable for the host.&rdquo;</span></p>
          </div>
        </div>
      </section>

      {/* LEADERSHIP — single horizontal founder card */}
      <section className="py-20 md:py-28 bg-[#F4DCD8]/35">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <p className="pill">Leadership</p>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[#3B312D] leading-[1.05]">
              Led with vision, <span className="italic text-[#B65980]">shaped with care</span>.
            </h2>
            <p className="mt-5 text-[16px] text-[#3B312D]/80 leading-relaxed">
              Petals &amp; Promises is guided by a creative-first approach where every celebration is planned with emotion, design sense,
              coordination, and a deep respect for detail.
            </p>
          </div>

          <Reveal className="mt-14 mx-auto bg-white rounded-[24px] border border-[rgba(182,89,128,0.18)] shadow-[0_18px_44px_-26px_rgba(59,49,45,0.18)] overflow-hidden flex flex-col md:flex-row" style={{ maxWidth: 1180 }} data-testid={`founder-card-${founder.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="md:w-[34%] shrink-0 bg-[#F4DCD8]/30 self-stretch">
              <Img
                imageKey="founder.shubham"
                defaultSrc={founder.image}
                defaultAlt={founder.name}
                alt={founder.name}
                className="w-full h-72 md:h-full"
                position="center 22%"
              />
            </div>
            <div className="p-7 md:p-10 lg:p-12 flex flex-col justify-center md:w-[66%]">
              <span className="inline-block w-12 h-[2px] bg-[#B65980]/70 mb-5" aria-hidden />
              <h3 className="font-display text-[30px] md:text-[34px] text-[#B65980] leading-[1.1]">{founder.name}</h3>
              <p className="mt-2 text-[11px] tracking-[0.26em] uppercase text-[#3B312D]/70 font-semibold">{founder.role}</p>
              <p className="mt-5 text-[14.5px] md:text-[15px] text-[#3B312D]/85 leading-[1.75]">
                Shubham brings together brand thinking, visual direction, and event storytelling to shape celebrations that feel
                personal, premium, and beautifully managed. His approach blends emotion, aesthetics, and on-ground coordination
                so every event feels effortless for the host and memorable for the guests.
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5" data-testid="founder-chips">
                {["Creative Direction", "Event Storytelling", "Guest Experience"].map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11.5px] font-medium tracking-[0.04em]"
                    style={{
                      background: "rgba(244, 220, 216, 0.45)",
                      border: "1px solid rgba(182, 89, 128, 0.32)",
                      color: "#3B312D",
                    }}
                  >
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-[12.5px] text-[#3B312D]/65 italic leading-relaxed">
                Guiding every celebration with design sense, calm planning, and attention to detail.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS — same refined blush band as Home */}
      <StatsCard stats={STATS} />

      {/* Why clients trust us */}
      <section className="py-20 md:py-28 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="pill">Why Clients Trust Us</p>
          <h2 className="font-display text-4xl md:text-5xl mt-5 text-[#3B312D] max-w-3xl">A calm partner across every layer of your event.</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={i * 80} className="lux-card p-7 h-full">
                <p className="text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">Why {i + 1}</p>
                <h4 className="font-display text-2xl mt-3 text-[#3B312D]">{w.title}</h4>
                <p className="mt-3 text-[14.5px] text-[#3B312D]/80 leading-relaxed">{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="py-20 md:py-28 bg-[#F4DCD8]/35 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <p className="pill mx-auto">Our Promise</p>
          <p className="mt-8 font-editorial italic text-3xl md:text-5xl text-[#3B312D] leading-[1.15]">
            &ldquo;Every celebration should feel effortless for the guest and unforgettable for the host.&rdquo;
          </p>
          <Link to="/contact" className="btn-primary mt-10" data-testid="about-cta">Begin Your Celebration <ArrowRight size={14} /></Link>
        </div>
      </section>

      <BrochureCTA />
      <FinalCTA />
    </div>
  );
}
